/**
 * Image compression + upload helpers used across verification, vehicle
 * images, inspections, and any other flow that captures photos.
 *
 * One pipeline, every caller: compress until the budget fits → upload →
 * prove the object exists in Storage → only then return the path.
 *
 * A row in `vehicle_images` whose object is missing is how Qualitas saw
 * "Object not found" on a coverage review: the client wrote the row after a
 * payload the bucket had already refused (10 MiB cap). Throwing IMAGE_TOO_LARGE
 * after a single 1920/0.75 pass was the other half of that hole — Pixel
 * originals can stay huge after one pass. The loop below is the product
 * behaviour: keep reducing quality/edge until the file fits, then refuse
 * only if even the last step exceeds the hard cap.
 */
import { Platform } from 'react-native'
import { File as ExpoFile } from 'expo-file-system'
import { ImageManipulator, SaveFormat } from 'expo-image-manipulator'
import { ServiceError } from '../errors/service-error'
import {
  storageObjectState,
  uploadBytesWithSessionRecovery,
  type StorageObjectState,
} from './storage-verify'

/**
 * Minimum post-compression byte size. Anything smaller than this is almost
 * certainly a placeholder image or a blank frame captured by accident.
 * Identity-document OCR needs real image data to read numbers off the card.
 */
const IMAGE_MIN_BYTES = 15 * 1024 // 15 KiB

export type ImageBudget = {
  /** Longest side of the first (highest quality) pass. */
  maxDimension: number
  /** Shorter side after the first pass. Below this the source is not usable. */
  minDimension: number
  /** Keep stepping quality/size down until at or below this. */
  targetBytes: number
  /** Bucket/API ceiling. Throw IMAGE_TOO_LARGE only after the last step. */
  hardMaxBytes: number
}

/**
 * Named budgets. `identity` stays the historical 5 MiB OCR cap.
 * `listing` / `inspection` / `document` match the 10 MiB vehicles /
 * inspections buckets and a 1.5 MiB target so a coverage email actually
 * renders in a reviewer's inbox.
 */
export const IMAGE_BUDGETS = {
  identity: {
    maxDimension: 1920,
    minDimension: 800,
    targetBytes: 5 * 1024 * 1024,
    hardMaxBytes: 5 * 1024 * 1024,
  },
  listing: {
    maxDimension: 1920,
    minDimension: 640,
    targetBytes: 1_500_000,
    hardMaxBytes: 10 * 1024 * 1024,
  },
  inspection: {
    maxDimension: 1920,
    minDimension: 640,
    targetBytes: 1_500_000,
    hardMaxBytes: 10 * 1024 * 1024,
  },
  document: {
    maxDimension: 1920,
    minDimension: 800,
    targetBytes: 1_500_000,
    hardMaxBytes: 10 * 1024 * 1024,
  },
  chat: {
    maxDimension: 1200,
    minDimension: 320,
    targetBytes: 800_000,
    hardMaxBytes: 10 * 1024 * 1024,
  },
  avatar: {
    maxDimension: 384,
    minDimension: 64,
    targetBytes: 200_000,
    hardMaxBytes: 2 * 1024 * 1024,
  },
  logo: {
    maxDimension: 512,
    minDimension: 64,
    targetBytes: 250_000,
    hardMaxBytes: 2 * 1024 * 1024,
  },
} as const satisfies Record<string, ImageBudget>

export type ImageBudgetName = keyof typeof IMAGE_BUDGETS

export type ImageTransform = {
  crop?: { originX: number; originY: number; width: number; height: number }
  rotateDegrees?: number
  /** Exact square edge. Avatars use this so faces are not letterboxed. */
  square?: number
}

type CompressStep = { width: number; height?: number; compress: number }

function stepsFor(budget: ImageBudget, transform?: ImageTransform): CompressStep[] {
  if (transform?.square) {
    const edge = transform.square
    return [
      { width: edge, height: edge, compress: 0.8 },
      { width: edge, height: edge, compress: 0.65 },
      { width: edge, height: edge, compress: 0.5 },
    ]
  }
  const max = budget.maxDimension
  const floor = budget.minDimension
  const candidates: CompressStep[] = [
    { width: max, compress: 0.75 },
    { width: max, compress: 0.6 },
    { width: Math.min(1600, max), compress: 0.55 },
    { width: Math.min(1280, max), compress: 0.5 },
    { width: Math.min(1280, max), compress: 0.4 },
    { width: Math.max(floor, Math.min(1024, max)), compress: 0.4 },
  ]
  const seen = new Set<string>()
  const steps: CompressStep[] = []
  for (const step of candidates) {
    if (step.width < floor) continue
    const key = `${step.width}:${step.compress}`
    if (seen.has(key)) continue
    seen.add(key)
    steps.push(step)
  }
  return steps
}

export interface CompressResult {
  uri: string
  bytes: Uint8Array
  sizeBytes: number
  width: number
  height: number
}

export async function readUriBytes(uri: string): Promise<Uint8Array> {
  if (Platform.OS === 'web' || uri.startsWith('blob:') || uri.startsWith('data:')) {
    const res = await fetch(uri)
    if (!res.ok) {
      throw new ServiceError({
        message: 'No se pudo leer el archivo seleccionado.',
        code: 'FILE_READ_FAILED',
        statusCode: 400,
      })
    }
    return new Uint8Array(await res.arrayBuffer())
  }

  const file = new ExpoFile(uri)
  const buffer = await file.arrayBuffer()
  return new Uint8Array(buffer)
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]!)
  }
  return btoa(binary)
}

function resolveBudget(budget: ImageBudgetName | ImageBudget = 'identity'): ImageBudget {
  return typeof budget === 'string' ? IMAGE_BUDGETS[budget] : budget
}

/**
 * Compress an image URI to JPEG until it fits `targetBytes`.
 *
 * The first pass is the product quality (1920 / 0.75). Only if that still
 * exceeds the target do we step quality and then edge down. IMAGE_TOO_LARGE
 * is the last resort, after the last step, against `hardMaxBytes` — never
 * after a single pass that the bucket would have refused anyway.
 */
export async function compressImage(
  uri: string,
  budget: ImageBudgetName | ImageBudget = 'identity',
  transform?: ImageTransform,
): Promise<CompressResult> {
  const limits = resolveBudget(budget)
  const steps = stepsFor(limits, transform)
  // `maxDimension` means the LONGEST side (see ImageBudget). Resizing by width
  // alone only honoured that for landscape captures: a portrait phone photo
  // carries its long side in `height`, so `resize({ width: 1920 })` left a
  // 1920x4171 eight-megapixel file behind. Those bytes are what the coverage
  // review has to jpeg-decode on the server, at ~0.5s of CPU each, and ten of
  // them blew the edge worker's budget (HTTP 546 WORKER_RESOURCE_LIMIT). Probe
  // once so every step caps the long side regardless of orientation.
  let portrait = false
  if (!transform?.square) {
    // A requested 90°/270° rotation swaps the axes: a tall portrait capture
    // becomes a wide landscape once rotated, so the side that is longest AFTER
    // the user's transform decides the resize axis. 009354aa probed the raw
    // capture and resized by height for any portrait input; that left the
    // rotated image's new longest side above maxDimension.
    const rotation = transform?.rotateDegrees ?? 0
    const normalizedRotation = ((rotation % 360) + 360) % 360
    const swapsAxes = normalizedRotation === 90 || normalizedRotation === 270
    try {
      const probe = await ImageManipulator.manipulate(uri).renderAsync()
      const probeWidth = probe.width ?? 0
      const probeHeight = probe.height ?? 0
      portrait = swapsAxes ? probeWidth > probeHeight : probeHeight > probeWidth
    } catch {
      portrait = false
    }
  }
  let firstMinSide: number | null = null
  let last: CompressResult | null = null

  for (const step of steps) {
    // Expo loads already-oriented pixels: iOS adds ImageFixOrientationTransformer,
    // Android's expo-image-loader uses Glide's EXIF decoder, and web uses the
    // browser decoder. Reapplying the original EXIF here rotates/mirrors twice.
    // Only explicit user transforms belong after this normalization.
    const pipeline = ImageManipulator.manipulate(uri)
    if (transform?.crop) pipeline.crop(transform.crop)
    if (transform?.rotateDegrees) pipeline.rotate(transform.rotateDegrees)
    const resizeTarget = step.height
      ? { width: step.width, height: step.height }
      : portrait
        ? { height: step.width }
        : { width: step.width }
    const imageRef = await pipeline.resize(resizeTarget).renderAsync()
    const outputWidth = imageRef.width ?? step.width
    const outputHeight = imageRef.height ?? step.height ?? step.width
    const minSide = Math.min(outputWidth, outputHeight)
    if (firstMinSide === null) {
      firstMinSide = minSide
      if (minSide > 0 && minSide < limits.minDimension) {
        throw ServiceError.validation('IMAGE_TOO_SMALL', {
          width: outputWidth,
          height: outputHeight,
          minDimension: limits.minDimension,
        })
      }
    }

    const compressed = await imageRef.saveAsync({
      format: SaveFormat.JPEG,
      compress: step.compress,
    })
    // `expo-file-system` v19's `File` constructor calls a native-only
    // validatePath method on web. Keep all browser file/blob reads on fetch.
    const bytes = await readUriBytes(compressed.uri)
    last = {
      uri: compressed.uri,
      bytes,
      sizeBytes: bytes.length,
      width: compressed.width ?? outputWidth,
      height: compressed.height ?? outputHeight,
    }
    if (bytes.length <= limits.targetBytes) break
  }

  if (!last) {
    throw ServiceError.validation('IMAGE_TOO_LARGE', {
      sizeBytes: 0,
      maxBytes: limits.hardMaxBytes,
    })
  }

  if (last.sizeBytes > limits.hardMaxBytes) {
    throw ServiceError.validation('IMAGE_TOO_LARGE', {
      sizeBytes: last.sizeBytes,
      maxBytes: limits.hardMaxBytes,
    })
  }

  if (last.sizeBytes < IMAGE_MIN_BYTES) {
    // Detect blank / placeholder / extremely low-detail captures. A 15 KiB
    // JPEG at 1920px is almost certainly a single-colour background.
    throw ServiceError.validation('IMAGE_LOW_QUALITY', {
      sizeBytes: last.sizeBytes,
      minBytes: IMAGE_MIN_BYTES,
    })
  }

  return last
}

/**
 * After upload(), prove the object is actually in the bucket.
 *
 * Storage can refuse an oversize body (10 MiB on `vehicles`) while a caller
 * that ignored the error still wrote `vehicle_images`. Qualitas then opened
 * a signed URL to a key that was never there. This is the client-side half
 * of that invariant; the database trigger is the other half.
 *
 * The check is DEFINITIVE, never heuristic. The previous implementation
 * listed the folder with a `search` param and parsed `metadata.size` — a
 * false negative on a real object (2026-09-07, front_right.jpg existed two
 * seconds after a server-side copy and the check said it did not) stranded a
 * real host for seven hours.
 *
 * "Not provable" is NOT "missing". A storage consistency gap, a timeout or a
 * dropped request must never be reported as a lost file: that is exactly how
 * TAGO-MOBILE-4E fired on 2026-09-21 (object present, `owner_id` matched, the
 * check failed two seconds after upload). When the answer is inconclusive the
 * flow continues and the server-side trigger
 * (`private.assert_row_storage_object_exists`, HINT `storage_object_missing`)
 * decides with the truth.
 */
export async function assertObjectStored(
  bucket: string,
  storagePath: string,
): Promise<StorageObjectState> {
  const state = await storageObjectState(bucket, storagePath)

  switch (state) {
    case 'exists':
      return state
    case 'missing':
      throw new ServiceError({
        message: `storage object missing after upload (${bucket}/${storagePath})`,
        code: 'STORAGE_OBJECT_MISSING',
        statusCode: 500,
        userMessageKey: 'listing.photo_upload_network',
      })
    case 'unknown':
      // Not provable is not missing. The flow continues and the server-side
      // trigger (HINT `storage_object_missing`) decides with the truth. The
      // returned state is the caller's hook for observability if it wants one.
      return state
  }
}

/**
 * Compress an image and upload it to a Supabase Storage bucket at the given
 * path. Returns the storage path on success, throws ServiceError on failure.
 */
export async function compressAndStore(
  uri: string,
  bucket: string,
  storagePath: string,
  budget: ImageBudgetName | ImageBudget = 'identity',
  transform?: ImageTransform,
): Promise<CompressResult & { storagePath: string }> {
  const compressed = await compressImage(uri, budget, transform)

  // Session gate + one auth-denied recovery retry, shared with every storage
  // write (see lib/utils/storage-verify.ts). A degraded session is the class
  // that produced "new row violates row-level security policy" nine times in
  // one evening for a real host; the retry happens silently, before the error
  // is ever surfaced.
  const { error: uploadError } = await uploadBytesWithSessionRecovery(
    bucket,
    storagePath,
    compressed.bytes,
    {
      contentType: 'image/jpeg',
      upsert: true,
    },
  )

  if (uploadError) {
    throw new ServiceError({
      message: `Image upload failed: ${uploadError.message ?? 'unknown'}`,
      code: 'UPLOAD_FAILED',
      statusCode: 500,
      cause: uploadError,
    })
  }

  await assertObjectStored(bucket, storagePath)
  return { ...compressed, storagePath }
}

/**
 * Compress an image and upload it to a Supabase Storage bucket at the given
 * path. Returns the storage path on success, throws ServiceError on failure.
 */
export async function compressAndUpload(
  uri: string,
  bucket: string,
  storagePath: string,
  budget: ImageBudgetName | ImageBudget = 'identity',
  transform?: ImageTransform,
): Promise<string> {
  const stored = await compressAndStore(uri, bucket, storagePath, budget, transform)
  return stored.storagePath
}
